import { Toast } from '@components/Toast';
import { faPencil, faRight, faTrash } from '@fortawesome/pro-light-svg-icons';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { UpdateSegment } from '@partful/entities/instructions/segment';
import { useConfigurationContext } from '@partful/features/configurations/lib/configurationProvider';
import { useDeleteSegment } from '@partful/features/instructions/segments/lib/useDeleteSegment';
import { useUpdateSegmentContent } from '@partful/features/instructions/segments/lib/useUpdateSegmentContent';
import { useListSteps } from '@partful/features/instructions/steps/lib/useListSteps';
import { SegmentModel, SegmentWithContentModel, VersionModel } from '@partful/instructions-domain-models';
import { classNames } from '@partful/shared/lib/classNames';
import { Button } from '@partful/shared/ui/Button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogPanel } from '@partful/shared/ui/Dialog/Dialog';
import { Typography } from '@partful/shared/ui/Typography';
import { AxiosError } from 'axios';
import { useMemo, useState } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { SegmentForm } from '../SegmentForm/SegmentForm';
import { StepList } from '../StepList/StepList';
import styles from './SegmentsListItem.module.scss';

export type SegmentsListItemProps = {
  version: VersionModel;
  segment: SegmentModel & Partial<SegmentWithContentModel>;
  languageCode: string;
};

export function SegmentsListItem({ version, segment, languageCode }: Readonly<SegmentsListItemProps>) {
  const { t: i18n } = useTranslation();
  const { productExternalID, PID } = useConfigurationContext();

  const [showSteps, setShowSteps] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const { data: steps } = useListSteps(version.instructionId, version.id, segment.id, languageCode);

  const {
    mutateAsync: updateSegmentContent,
    isLoading: isUpdating,
    isError: isErrorUpdating,
    isSuccess: isSuccessUpdating,
    error: updateError,
  } = useUpdateSegmentContent();

  const handleUpdateSegment: SubmitHandler<UpdateSegment> = async ({ name, description }) => {
    try {
      if (segment.content != null) {
        await updateSegmentContent({
          instructionId: version.instructionId,
          versionId: version.id,
          segmentId: segment.id,
          languageCode: segment.content.languageCode,
          updateArgs: {
            name,
            description: description!,
            __expectedVersion: segment.content.__version,
          },
        });
        setIsEditing(false);
      }
    } catch (e) {
      // Don't need to handle this here
    }
  };

  const isConflictError = useMemo(() => {
    if (updateError instanceof AxiosError) {
      return updateError.status === 409;
    }
  }, [updateError]);

  const {
    mutateAsync: deleteSegment,
    isLoading: isDeleting,
    isError: isErrorDeleting,
    isSuccess: isSuccessDeleting,
  } = useDeleteSegment();

  const handleDelete = async () => {
    try {
      await deleteSegment({
        versionId: segment.versionId,
        instructionId: version.instructionId,
        segmentId: segment.id,
      });
    } catch (e) {
      // Don't need to handle this here
    }
  };

  return (
    <div className={styles.segment} data-testid={`segment-${segment.order}`}>
      <div className={styles.detailsContainer}>
        <Button
          type="button"
          variant="tertiary"
          icon
          className={styles.expander}
          aria-label={i18n('instructions:manage.version.segments.toggleSteps')}
          aria-controls={`steps-for-${segment.id}`}
          aria-expanded={showSteps}
          onClick={() => {
            setShowSteps((current) => !current);
          }}
        >
          <FontAwesomeIcon icon={faChevronDown} />
        </Button>
        <div className={styles.details}>
          {!isEditing && (
            <div className={styles.overview}>
              <div className={styles.fields}>
                <Typography fontWeight={600} className={styles.name}>
                  {segment.content?.name}
                </Typography>
                <Typography>{segment.content?.description}</Typography>
              </div>
              <div className={styles.buttons}>
                <Button
                  name="instructions_version_langandinstructions_edit_segment_click"
                  variant="secondary"
                  onClick={() => setIsEditing(true)}
                >
                  <FontAwesomeIcon icon={faPencil} />
                  {i18n('instructions:segment.actions.edit')}
                </Button>
                <Button
                  name="instructions_version_langandinstructions_stepbuilder_click"
                  variant="primary"
                  linkComponent={Link}
                  to={`/instructions/step-builder/${PID}/${version.instructionId}/${version.id}/${
                    segment.id
                  }?lang=${languageCode}&productName=${encodeURIComponent(productExternalID)}`}
                >
                  {i18n('instructions:manage.version.segments.stepBuilder')}
                  <FontAwesomeIcon icon={faRight} />
                </Button>
              </div>
            </div>
          )}
          {isEditing && (
            <div className={styles.editForm}>
              <Button
                name="instructions_version_langandinstructions_delete_segment_click"
                size="small"
                variant="destructive"
                onClick={() => setConfirmDelete(true)}
              >
                <FontAwesomeIcon icon={faTrash} />
                {i18n('instructions:segment.actions.delete')}
              </Button>
              <SegmentForm
                segment={segment}
                onSubmit={handleUpdateSegment}
                onCancel={() => setIsEditing(false)}
                disabled={isUpdating || isDeleting}
              />
            </div>
          )}
        </div>
      </div>
      <div id={`steps-for-${segment.id}`} className={classNames(styles.steps, showSteps ? styles.expanded : undefined)}>
        {steps != null && steps.length > 0 && (
          <Typography as="h3" fontWeight={600} className={styles.title}>
            {i18n('instructions:manage.version.segments.steps')}
          </Typography>
        )}
        <StepList steps={steps ?? []} isEditing={isEditing} segment={segment} version={version} />
      </div>
      <Dialog
        open={confirmDelete}
        modal
        onClose={/* istanbul ignore next - not testing native <dialog> behaviour */ () => setConfirmDelete(false)}
      >
        <DialogPanel>
          <DialogHeader>
            <Typography as="p" appearance="heading5" overflow="hidden" textOverflow="ellipsis">
              {i18n('instructions:segment.actions.deleteConfirm.title', { segmentName: segment.content?.name })}
            </Typography>
          </DialogHeader>
          <DialogContent>{i18n('instructions:segment.actions.deleteConfirm.message')}</DialogContent>
          <DialogFooter>
            <Button
              name="instructions_version_langandinstructions_delete_segment_confirm_click"
              data-testid="confirm-delete-btn"
              variant="destructive"
              disabled={isDeleting}
              onClick={handleDelete}
            >
              {i18n('instructions:common.delete')}
            </Button>
            <Button
              name="instructions_version_langandinstructions_delete_segment_cancel_click"
              disabled={isDeleting}
              variant="tertiary"
              onClick={/* istanbul ignore next */ () => setConfirmDelete(false)}
            >
              {i18n('instructions:common.cancel')}
            </Button>
          </DialogFooter>
        </DialogPanel>
      </Dialog>
      <Toast
        dependency={isErrorUpdating}
        severity="error"
        title={i18n(
          isConflictError
            ? 'instructions:segment.actions.saveConflictError.title'
            : 'instructions:segment.actions.saveError.title'
        )}
        message={i18n(
          isConflictError
            ? 'instructions:segment.actions.saveConflictError.message'
            : 'instructions:segment.actions.saveError.message'
        )}
        testId="update-segment-error-message"
      />
      <Toast
        dependency={isSuccessUpdating}
        severity="success"
        title={i18n('instructions:segment.actions.saveSuccess.title')}
        testId="update-segment-success-message"
      />
      <Toast
        dependency={isErrorDeleting}
        severity="error"
        title={i18n('instructions:segment.actions.deleteError.title')}
        message={i18n('instructions:segment.actions.deleteError.message')}
        testId="delete-segment-error-message"
      />
      <Toast
        dependency={isSuccessDeleting}
        severity="success"
        title={i18n('instructions:segment.actions.deleteSuccess.title')}
        testId="delete-segment-success-message"
      />
    </div>
  );
}
